import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

type AalLevel = 'aal1' | 'aal2' | null;

interface Account {
  id: string;
  email: string;
  email_normalized: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  // reference is org-scoped and stored ONLY on org_memberships.reference
  is_anonymized: boolean;
  auth_user_id: string | null;
  created_at: string;
  updated_at: string;
}

interface OrgMembership {
  id: string;
  account_id: string;
  org_id: string;
  role: 'org_admin' | 'member';
  joined_at: string | null;
  org?: {
    id: string;
    name: string;
  };
}

interface Profile {
  id: string;
  account_id: string;
  current_org_id: string | null;
  preferred_language?: string | null;
  terms_accepted_at?: string | null;
  terms_version?: string | null;
  privacy_accepted_at?: string | null;
  privacy_version?: string | null;
  notify_new_offers?: boolean;
  notify_new_listings?: boolean;
  notify_offer_expiry?: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  profile: Profile | null;
  account: Account | null;
  orgMemberships: OrgMembership[];
  currentOrgId: string | null;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  isOrgAdmin: boolean;
  loading: boolean;
  isAuthRefreshing: boolean;
  // MFA enforcement state
  currentAal: AalLevel;
  mfaRequired: boolean;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  checkMfaStatus: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: Error | null }>;
  switchOrg: (orgId: string) => Promise<{ error: Error | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [account, setAccount] = useState<Account | null>(null);
  const [orgMemberships, setOrgMemberships] = useState<OrgMembership[]>([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isOrgAdmin, setIsOrgAdmin] = useState(false);
  
  // MFA enforcement state
  const [currentAal, setCurrentAal] = useState<AalLevel>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  
  // Silent refresh flag: true during TOKEN_REFRESHED background refetch
  const [isAuthRefreshing, setIsAuthRefreshing] = useState(false);
  
  // Explicit readiness flags for fail-closed behavior
  const [initialCheckDone, setInitialCheckDone] = useState(false);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [mfaChecked, setMfaChecked] = useState(false);
  
  // Stale-update protection: increment on each auth state change
  const requestIdRef = useRef(0);
  
  // Synchronous user-id tracking to avoid stale-closure issues in onAuthStateChange
  const userIdRef = useRef<string | null>(null);

  // DEBUG flag: flip to true to trace auth events in console during development
  const DEBUG_AUTH = import.meta.env.DEV;

  const fetchProfile = useCallback(async (userId: string, requestId: number) => {
    try {
      // Fetch profile (auth/runtime fields only)
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();
      
      // Stale check: abort if a newer request has started
      if (requestId !== requestIdRef.current) return;
      
      if (import.meta.env.DEV) {
        console.debug('[auth] profiles fetch:', { data: profileData, error: profileError });
      }
      
      let fetchedIsOrgAdmin = false;
      let fetchedIsSuperAdmin = false;
      let fetchedMemberships: OrgMembership[] = [];
      
      if (profileData) {
        setProfile(profileData as Profile);
        
        // Fetch account if we have account_id - canonical source for identity
        if (profileData.account_id) {
          const { data: accountData, error: accountError } = await supabase
            .from('accounts')
            .select('*')
            .eq('id', profileData.account_id)
            .single();
          
          // Stale check
          if (requestId !== requestIdRef.current) return;
          
          if (import.meta.env.DEV) {
            console.debug('[auth] accounts fetch:', { data: accountData, error: accountError });
          }
          
          if (accountData) {
            setAccount(accountData as Account);
          }
          
          // Fetch org memberships - canonical source for org roles
          const { data: memberships, error: membershipsError } = await supabase
            .from('org_memberships')
            .select(`
              id,
              account_id,
              org_id,
              role,
              joined_at,
              org:orgs(id, name)
            `)
            .eq('account_id', profileData.account_id)
            .order('created_at', { ascending: true });
          
          // Stale check
          if (requestId !== requestIdRef.current) return;
          
          if (import.meta.env.DEV) {
            console.debug('[auth] org_memberships fetch:', { data: memberships, error: membershipsError });
          }
          
          if (memberships) {
            // Filter to ACTIVE memberships only (joined_at IS NOT NULL)
            // This enforces Invariant #16: Membership Activation Gate
            const activeMemberships = memberships.filter(m => m.joined_at !== null);
            fetchedMemberships = activeMemberships as unknown as OrgMembership[];
            setOrgMemberships(fetchedMemberships);
            
            // Determine org admin status for current org (must be active member)
            const effectiveOrgId = profileData.current_org_id;
            const currentMembership = fetchedMemberships.find(m => m.org_id === effectiveOrgId);
            fetchedIsOrgAdmin = currentMembership?.role === 'org_admin';
            setIsOrgAdmin(fetchedIsOrgAdmin);
          }
          
          // Check for platform role (super_admin) - canonical source
          const { data: platformRoles, error: rolesError } = await supabase
            .from('account_roles')
            .select('role')
            .eq('account_id', profileData.account_id);
          
          // Stale check
          if (requestId !== requestIdRef.current) return;
          
          if (import.meta.env.DEV) {
            console.debug('[auth] account_roles fetch:', { data: platformRoles, error: rolesError });
          }
          
          fetchedIsSuperAdmin = platformRoles?.some(r => r.role === 'super_admin') || false;
          setIsSuperAdmin(fetchedIsSuperAdmin);
        }
        
        // Auto-set current_org_id if null and user has active memberships
        // OR if current_org_id points to an org where user is not an active member (fail-safe)
        const needsOrgFallback = !profileData.current_org_id || 
          (profileData.current_org_id && !fetchedMemberships.find(m => m.org_id === profileData.current_org_id));
        
        if (needsOrgFallback && fetchedMemberships.length > 0) {
          const firstActiveOrgId = fetchedMemberships[0].org_id;
          if (import.meta.env.DEV) {
            console.debug('[auth] Falling back to first active membership:', firstActiveOrgId, 
              'reason:', !profileData.current_org_id ? 'no current_org_id' : 'current_org_id not in active memberships');
          }
          
          // Show toast if this is a fallback from invalid org (not just initial setup)
          if (profileData.current_org_id && !fetchedMemberships.find(m => m.org_id === profileData.current_org_id)) {
            toast.warning('Organization access changed', {
              description: 'You no longer have active access to the selected organization. Switched to your first available organization.',
              duration: 5000,
            });
          }
          
          const { error: updateError } = await supabase
            .from('profiles')
            .update({ current_org_id: firstActiveOrgId })
            .eq('id', userId);
          
          // Stale check
          if (requestId !== requestIdRef.current) return;
          
          if (import.meta.env.DEV && updateError) {
            console.error('[auth] Failed to auto-set current_org_id:', updateError);
          }
          
          if (!updateError) {
            // Update local profile state
            setProfile({ ...profileData, current_org_id: firstActiveOrgId } as Profile);
          }
        } else if (needsOrgFallback && fetchedMemberships.length === 0 && !fetchedIsSuperAdmin) {
          // No active memberships and not super admin - clear org context
          if (profileData.current_org_id) {
            if (import.meta.env.DEV) {
              console.debug('[auth] No active memberships, clearing current_org_id');
            }
            toast.warning('Organization access revoked', {
              description: 'You no longer have active access to any organization.',
              duration: 5000,
            });
            
            const { error: updateError } = await supabase
              .from('profiles')
              .update({ current_org_id: null })
              .eq('id', userId);
            
            if (requestId !== requestIdRef.current) return;
            
            if (!updateError) {
              setProfile({ ...profileData, current_org_id: null } as Profile);
            }
          }
        }
      }

      // Stale check before final state update
      if (requestId !== requestIdRef.current) return;
      
      // Set admin status based on new tables only (no legacy fallback in frontend)
      setIsAdmin(fetchedIsOrgAdmin || fetchedIsSuperAdmin);
    } finally {
      // Always mark as loaded (even on error = fail-closed)
      if (requestId === requestIdRef.current) {
        setProfileLoaded(true);
      }
    }
  }, []);

  // Check MFA status - determines if user needs to complete MFA
  const checkMfaStatus = useCallback(async (requestId: number) => {
    try {
      const [aalResult, factorsResult] = await Promise.all([
        supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
        supabase.auth.mfa.listFactors(),
      ]);

      // Stale check
      if (requestId !== requestIdRef.current) return;

      if (aalResult.error || factorsResult.error) {
        console.error('MFA status check failed:', aalResult.error || factorsResult.error);
        return;
      }

      const currentLevel = aalResult.data?.currentLevel || null;
      setCurrentAal(currentLevel);

      // Check if user has any verified TOTP factors
      const hasVerifiedFactor = factorsResult.data?.totp?.some(f => f.status === 'verified') || false;

      // MFA is required if user has verified factor but is only at AAL1
      const needsMfa = hasVerifiedFactor && currentLevel === 'aal1';
      setMfaRequired(needsMfa);

      if (needsMfa && DEBUG_AUTH) {
        console.debug('[auth] MFA required: user has verified factor but is at AAL1');
      }
    } catch (error) {
      console.error('Error checking MFA status:', error);
    } finally {
      // Always mark as checked (even on error = fail-closed)
      if (requestId === requestIdRef.current) {
        setMfaChecked(true);
      }
    }
  }, []);

  // Public checkMfaStatus wrapper (no requestId param exposed)
  const checkMfaStatusPublic = useCallback(async () => {
    const requestId = requestIdRef.current;
    await checkMfaStatus(requestId);
  }, [checkMfaStatus]);

  const refreshProfile = useCallback(async () => {
    if (user) {
      // Increment request ID to invalidate any in-flight requests
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      
      // Reset readiness flags before refetch
      setProfileLoaded(false);
      setMfaChecked(false);
      
      await Promise.all([
        fetchProfile(user.id, requestId),
        checkMfaStatus(requestId)
      ]);
    }
  }, [user, fetchProfile, checkMfaStatus]);

  // Switch current org
  const switchOrg = useCallback(async (orgId: string) => {
    if (!user) {
      return { error: new Error('Not authenticated') };
    }

    // Verify user has ACTIVE membership in this org (joined_at not null)
    // Filter already applied to orgMemberships, but defense-in-depth
    const membership = orgMemberships.find(m => m.org_id === orgId && m.joined_at !== null);
    if (!membership && !isSuperAdmin) {
      return { error: new Error('Not an active member of this organization') };
    }

    // Update current_org_id on profile
    const { error } = await supabase
      .from('profiles')
      .update({ current_org_id: orgId })
      .eq('id', user.id);

    if (error) {
      return { error };
    }

    // Refresh profile to update state
    const requestId = requestIdRef.current;
    await fetchProfile(user.id, requestId);

    return { error: null };
  }, [user, orgMemberships, isSuperAdmin, fetchProfile]);

  useEffect(() => {
    // Reset readiness on mount
    setInitialCheckDone(false);
    setProfileLoaded(false);
    setMfaChecked(false);
    
    // Set up auth listener with proper cleanup
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Increment request ID on auth change to invalidate stale fetches
      requestIdRef.current += 1;
      const newRequestId = requestIdRef.current;

      // ── Ref-based same-user detection ──
      // React state (user?.id) suffers from stale closures in this callback.
      // userIdRef is updated synchronously, so it always reflects the latest value.
      const incomingUserId = session?.user?.id ?? null;
      const previousUserId = userIdRef.current;
      userIdRef.current = incomingUserId; // Update ref BEFORE async setState

      if (DEBUG_AUTH) {
        console.debug('[auth] onAuthStateChange', {
          event,
          timestamp: new Date().toISOString(),
          pathname: window.location.pathname,
          incomingUserId,
          previousUserId,
          profileLoaded,
          mfaChecked,
        });
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Same-user: both IDs exist and match (covers TOKEN_REFRESHED,
        // SIGNED_IN-as-recovery, and any session-maintenance event on tab focus).
        const isSameUser = previousUserId != null && incomingUserId === previousUserId;
        const isProfileReady = profileLoaded && mfaChecked;

        // Conservative maintenance: even if previousUserId was null (race between
        // getSession and onAuthStateChange), treat as maintenance when we already
        // have valid profile/MFA data AND there is a concrete session user.
        const canTreatAsMaintenance = incomingUserId != null && isProfileReady;

        // ── Security-transition guard ──
        // MFA_CHALLENGE_VERIFIED (and similar security-sensitive events) must
        // always trigger an immediate MFA status refresh so ProtectedRoute
        // gates reflect the new AAL level. These are NOT silent maintenance.
        const isSecurityTransition = event === 'MFA_CHALLENGE_VERIFIED';

        if ((isSameUser || canTreatAsMaintenance) && !isSecurityTransition) {
          // Session maintenance: silent background refresh.
          // Do NOT reset readiness flags — loading stays false,
          // ProtectedRoute keeps children mounted, local UI state preserved.
          if (DEBUG_AUTH) {
            console.debug('[auth] Silent maintenance refresh (same user, profile ready)', {
              event, requestId: newRequestId, isSameUser, canTreatAsMaintenance,
            });
          }
          setIsAuthRefreshing(true);
          setTimeout(() => {
            Promise.all([
              fetchProfile(session.user.id, newRequestId),
              checkMfaStatus(newRequestId),
            ])
              .catch((err) => console.warn('[auth] Silent refresh failed:', err))
              .finally(() => {
                if (newRequestId === requestIdRef.current) {
                  setIsAuthRefreshing(false);
                }
              });
          }, 0);
        } else {
          // Real auth transition: different user, first load, or security event.
          // Full reset with loading state is expected here.
          if (DEBUG_AUTH) {
            console.debug('[auth] Full auth transition', {
              event, isSameUser, canTreatAsMaintenance, isSecurityTransition,
              requestId: newRequestId,
            });
          }
          setIsAuthRefreshing(false);
          setProfileLoaded(false);
          setMfaChecked(false);
          setTimeout(() => {
            Promise.all([
              fetchProfile(session.user.id, newRequestId),
              checkMfaStatus(newRequestId),
            ])
              .catch((err) => console.warn('[auth] Auth transition refresh failed:', err));
          }, 0);
        }
      } else {
        // No user — clear state and mark as ready
        if (DEBUG_AUTH) {
          console.debug('[auth] No user — clearing state', { event });
        }
        userIdRef.current = null;
        setProfile(null);
        setAccount(null);
        setOrgMemberships([]);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsOrgAdmin(false);
        setCurrentAal(null);
        setMfaRequired(false);
        setIsAuthRefreshing(false);
        setProfileLoaded(true);
        setMfaChecked(true);
      }
    });

    // Initial session check
    supabase.auth.getSession().then(({ data: { session } }) => {
      const currentRequestId = requestIdRef.current;
      userIdRef.current = session?.user?.id ?? null;
      
      if (DEBUG_AUTH) {
        console.debug('[auth] Initial getSession', {
          hasUser: !!session?.user,
          userId: session?.user?.id ?? null,
          pathname: window.location.pathname,
        });
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        Promise.all([
          fetchProfile(session.user.id, currentRequestId),
          checkMfaStatus(currentRequestId)
        ]).catch((err) => console.warn('[auth] Initial session fetch failed:', err));
      } else {
        // No user — mark as ready
        setProfileLoaded(true);
        setMfaChecked(true);
      }
      
      setInitialCheckDone(true);
    }).catch((err) => {
      console.warn('[auth] getSession() rejected:', err);
      // Treat as unauthenticated — fail closed, never leave app stuck in Loading
      setSession(null);
      setUser(null);
      userIdRef.current = null;
      setProfileLoaded(true);
      setMfaChecked(true);
      setIsAuthRefreshing(false);
      setInitialCheckDone(true);
    });

    // Cleanup: unsubscribe on unmount
    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile, checkMfaStatus]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  };

  const signOut = async () => {
    await supabase.auth.signOut();
    userIdRef.current = null;
    setProfile(null);
    setAccount(null);
    setOrgMemberships([]);
    setIsAdmin(false);
    setIsSuperAdmin(false);
    setIsOrgAdmin(false);
    setCurrentAal(null);
    setMfaRequired(false);
    setIsAuthRefreshing(false);
  };

  const resetPassword = async (email: string) => {
    try {
      const response = await supabase.functions.invoke('password-recovery', {
        body: { email },
      });
      if (response.error) {
        return { error: response.error };
      }
      return { error: null };
    } catch (err) {
      return { error: new Error('An unexpected error occurred. Please try again.') };
    }
  };

  // Compute current org ID from profile - prefer current_org_id only
  const currentOrgId = profile?.current_org_id || null;
  
  // Derive loading: true until auth is fully resolved
  // - Initial check not done: loading
  // - User exists but profile/MFA not ready: loading
  // - No user after initial check: not loading
  const loading = !initialCheckDone || (!!user && (!profileLoaded || !mfaChecked));

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      profile,
      account,
      orgMemberships,
      currentOrgId,
      isAdmin, 
      isSuperAdmin,
      isOrgAdmin,
      loading,
      isAuthRefreshing,
      currentAal,
      mfaRequired,
      signIn,
      signOut, 
      refreshProfile,
      checkMfaStatus: checkMfaStatusPublic,
      resetPassword,
      switchOrg,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
